import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchScheduleProducts,
  createScheduleProduct,
  updateScheduleProduct,
  deleteScheduleProduct,
} from "./thunks";
import { Product } from "@/types";
import { toast } from "react-toastify";

interface PaginatedProducts {
  data: Product[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface ScheduleProductState {
  products: PaginatedProducts;
  loading: boolean;
  error: string | null;
}

const initialState: ScheduleProductState = {
  products: { data: [] },
  loading: false,
  error: null,
};

const scheduleProductSlice = createSlice({
  name: "scheduleProducts",
  initialState,
  reducers: {
    clearScheduleProducts: (state) => {
      state.products = { data: [] };
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchScheduleProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchScheduleProducts.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            state.products = { data: payload.data };
          } else {
            state.products = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchScheduleProducts.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })

      // Create
      .addCase(createScheduleProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createScheduleProduct.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.products.data.push(action.payload.data);
          toast.success("Produto adicionado com sucesso!");
        },
      )
      .addCase(createScheduleProduct.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao adicionar produto!");
      })

      // Update
      .addCase(updateScheduleProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateScheduleProduct.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const index = state.products.data.findIndex(
            (p) => p.id == action.payload.data.id,
          );
          if (index !== -1) {
            state.products.data[index] = action.payload.data;
          }
          toast.success("Produto atualizado com sucesso!");
        },
      )
      .addCase(updateScheduleProduct.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao atualizar produto!");
      })

      // Delete
      .addCase(deleteScheduleProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteScheduleProduct.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.products.data = state.products.data.filter(
            (p) => String(p.id) !== String(action.payload.productId),
          );
          toast.success("Produto removido com sucesso!");
        },
      )
      .addCase(deleteScheduleProduct.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        toast.error("Erro ao remover produto!");
      });
  },
});

export const { clearScheduleProducts } = scheduleProductSlice.actions;
export default scheduleProductSlice.reducer;
